// Author: Ahsan Saleem
/**
 * ObjectView — the one object anatomy for all of BloomOS.
 *
 * The audit found ~30% of screens bypass the shared inspector and invent their own
 * detail layout, and that cross-object context is missing. This is the single
 * anatomy every major object (alert, task, PO, bill, lot, sales order, tax doc…)
 * renders through: identity + reference, state, provenance, the one primary action,
 * a prominent blocking exception with its required next action, key facts, related
 * objects (click-through), timeline, evidence, decisions and audit. It reads as a
 * full-screen object on mobile and inside the inspector sheet on desktop.
 */
import type { ReactNode } from 'react';
import { TriangleAlert } from 'lucide-react';
import type { Tone } from '../../ui/primitives.js';
import { StatusTag, KeyFacts, Timeline, type TimelineEvent } from './primitives.js';

export interface ObjectException { tone?: Tone; title: ReactNode; detail?: ReactNode; action?: ReactNode }

function Section({ title, children, muted }: { title: ReactNode; children: ReactNode; muted?: boolean }) {
  return (
    <section className={`v2-section${muted ? ' muted' : ''}`}>
      <div className="v2-section-h">{title}</div>
      <div className="v2-section-b">{children}</div>
    </section>
  );
}

export function ObjectView({
  kind, reference, title, statusType, status, provenance, primaryAction, exception,
  facts, related, timeline, evidence, decisions, audit, children,
}: {
  kind: string; reference: ReactNode; title?: ReactNode;
  statusType?: string; status?: string | null;
  provenance?: ReactNode; primaryAction?: ReactNode; exception?: ObjectException;
  facts?: Array<{ k: ReactNode; v: ReactNode }>;
  related?: ReactNode; timeline?: TimelineEvent[]; evidence?: ReactNode; decisions?: ReactNode; audit?: ReactNode; children?: ReactNode;
}) {
  return (
    <div className="v2-object">
      <header className="v2-object-head">
        <div className="v2-object-id">
          <span className="v2-object-kind">{kind}</span>
          <span className="v2-object-ref tnum">{reference}</span>
          {statusType && <StatusTag type={statusType} status={status} />}
        </div>
        {title && <h2 className="v2-object-title">{title}</h2>}
        {(provenance || primaryAction) && (
          <div className="v2-object-bar">
            <span className="v2-object-prov">{provenance}</span>
            {primaryAction && <span className="v2-object-primary">{primaryAction}</span>}
          </div>
        )}
      </header>

      {exception && (
        <div className={`v2-object-exception tone-${exception.tone ?? 'caution'}`} role="alert">
          <TriangleAlert size={16} aria-hidden />
          <div className="v2-exc-body">
            <div className="v2-exc-title">{exception.title}</div>
            {exception.detail && <div className="v2-exc-detail">{exception.detail}</div>}
          </div>
          {exception.action && <div className="v2-exc-action">{exception.action}</div>}
        </div>
      )}

      {facts && facts.length > 0 && <Section title="Key facts"><KeyFacts facts={facts} /></Section>}
      {related && <Section title="Related"><div className="v2-related">{related}</div></Section>}
      {timeline && timeline.length > 0 && <Section title="Timeline"><Timeline events={timeline} /></Section>}
      {evidence && <Section title="Evidence & documents">{evidence}</Section>}
      {decisions && <Section title="Decisions">{decisions}</Section>}
      {children}
      {audit && <Section title="Audit" muted>{audit}</Section>}
    </div>
  );
}
